import axios from './axiosSetup';
import API_BASE from './apiBase';

export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
export const STATUS_PING_INTERVAL_MS = 15 * 1000; // 15 seconds
export const BACKEND_SYNC_INTERVAL_MS = 60 * 1000; // Throttle backend activity sync to at most once per 60s
export const CROSS_TAB_SYNC_THROTTLE_MS = 2500; // Throttle localStorage write to at most once per 2.5s
export const READING_HEARTBEAT_MS = 60 * 1000; // 1 minute reading presence check
export const MAX_READING_NO_EVENT_MS = 15 * 60 * 1000; // Max reading window without any physical interaction (15 mins)
export const IDLE_CHECK_INTERVAL_MS = 5 * 1000; // Check idle status every 5s

let pingTimer = null;
let idleCheckTimer = null;
let readingTimer = null;

let lastPhysicalInteractionTime = Date.now();
let lastActivityTime = (() => {
  try {
    const saved = Number(localStorage.getItem('last_active_at') || 0);
    return saved > 0 ? saved : Date.now();
  } catch (e) {
    return Date.now();
  }
})();
let lastBackendSyncTime = 0;
let lastLocalStorageSyncTime = 0;
let lastMousePos = { x: 0, y: 0 };
let lastMouseMoveCheck = 0;
let isInitialized = false;

export function getLastActiveTime() {
  try {
    const shared = Number(localStorage.getItem('last_active_at') || 0);
    return Math.max(lastActivityTime, shared);
  } catch (e) {
    return lastActivityTime;
  }
}

export function isUserActiveRecently(windowMs = 60000) {
  const effective = getLastActiveTime();
  return (Date.now() - effective) < windowMs;
}

export function recordActivity(source = 'interaction') {
  const now = Date.now();
  if (source === 'interaction') {
    lastPhysicalInteractionTime = now;
  }
  lastActivityTime = now;

  // Cross-tab sync via localStorage (throttled)
  if (now - lastLocalStorageSyncTime > CROSS_TAB_SYNC_THROTTLE_MS) {
    lastLocalStorageSyncTime = now;
    try {
      localStorage.setItem('last_active_at', String(now));
    } catch (e) {}
  }

  // Backend sync (throttled)
  try {
    const token = localStorage.getItem('token');
    if (token && (now - lastBackendSyncTime > BACKEND_SYNC_INTERVAL_MS)) {
      lastBackendSyncTime = now;
      syncActivityToBackend();
    }
  } catch (e) {}
}

async function syncActivityToBackend() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const base = (API_BASE || '').replace(/\/+$/, '');
    // Try explicit /user/activity endpoint, fallback to /user/status?active=1
    await axios.post(
      `${base}/user/activity`,
      {},
      {
        timeout: 5000,
        skipLoading: true,
        headers: { 'X-User-Activity': '1' }
      }
    ).catch(async () => {
      await axios.get(`${base}/user/status`, {
        params: { active: 1 },
        timeout: 5000,
        skipLoading: true,
        headers: { 'X-User-Activity': '1' }
      });
    });
  } catch (err) {
    // 401 is handled by axios response interceptor
  }
}

export function handleSessionExpiration(reason = 'inactivity') {
  stopSessionWatcher();

  const msg = 'Your session has expired due to 30 minutes of inactivity. Please log in again.';
  try {
    sessionStorage.setItem('session_expired_message', msg);
    localStorage.setItem(
      'session_logout',
      JSON.stringify({ reason: 'timeout', message: msg, timestamp: Date.now() })
    );
  } catch (e) {}

  try {
    window.dispatchEvent(
      new CustomEvent('app-session-timeout', { detail: { reason, message: msg } })
    );
  } catch (e) {}

  try { localStorage.removeItem('token'); } catch (e) {}
  try { localStorage.removeItem('user'); } catch (e) {}
  try { localStorage.removeItem('tenant_id'); } catch (e) {}
  try { localStorage.removeItem('login_at'); } catch (e) {}
  try { localStorage.removeItem('last_active_at'); } catch (e) {}
  try { sessionStorage.removeItem('selectedConsentOverview'); } catch (e) {}
  try { if (window.__USER__) delete window.__USER__; } catch (e) {}
  try { if (window.USER) delete window.USER; } catch (e) {}

  try {
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  } catch (e) {
    window.location.reload();
  }
}

function checkInactivity() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const now = Date.now();
    const effectiveActive = getLastActiveTime();

    if (!effectiveActive || effectiveActive <= 0) {
      recordActivity('interaction');
      return;
    }

    const idleMs = now - effectiveActive;
    if (idleMs >= INACTIVITY_TIMEOUT_MS) {
      handleSessionExpiration('inactivity');
    }
  } catch (e) {}
}

function checkReadingPresence() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Reading detection: tab must be visible and focused, with user interaction in the last 15 minutes
    const isVisible = document.visibilityState === 'visible';
    const isFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    const now = Date.now();
    const timeSincePhysical = now - lastPhysicalInteractionTime;

    if (isVisible && isFocused && timeSincePhysical < MAX_READING_NO_EVENT_MS) {
      // User is actively reading on this foreground page
      recordActivity('reading');
    }
  } catch (e) {}
}

function startPing() {
  stopPing();
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    pingTimer = setInterval(async () => {
      try {
        const curToken = localStorage.getItem('token');
        if (!curToken) {
          stopPing();
          return;
        }

        const now = Date.now();
        const effectiveActive = getLastActiveTime();
        const wasActiveRecently = (now - effectiveActive) < BACKEND_SYNC_INTERVAL_MS;

        const headers = {};
        if (wasActiveRecently) {
          headers['X-User-Activity'] = '1';
          lastBackendSyncTime = now;
        }

        await axios.get(`${(API_BASE || '').replace(/\/+$/, '')}/user/status`, {
          timeout: 5000,
          skipLoading: true,
          authCheck: true,
          headers
        });
      } catch (e) {
        // Axios response interceptor handles 401
      }
    }, STATUS_PING_INTERVAL_MS);
  } catch (e) {}
}

function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

export function stopSessionWatcher() {
  stopPing();
  if (idleCheckTimer) {
    clearInterval(idleCheckTimer);
    idleCheckTimer = null;
  }
  if (readingTimer) {
    clearInterval(readingTimer);
    readingTimer = null;
  }
}

function startSessionWatcher() {
  stopSessionWatcher();
  startPing();

  // Periodic idle check
  idleCheckTimer = setInterval(checkInactivity, IDLE_CHECK_INTERVAL_MS);

  // Periodic reading detection check
  readingTimer = setInterval(checkReadingPresence, READING_HEARTBEAT_MS);
}

function onStorage(e) {
  try {
    if (!e) return;
    if (e.key === 'last_active_at') {
      const remoteTime = Number(e.newValue);
      if (remoteTime && remoteTime > lastActivityTime) {
        lastActivityTime = remoteTime;
      }
    }
    if (e.key === 'session_logout') {
      try {
        const payload = JSON.parse(e.newValue || '{}');
        if (payload && payload.message) {
          sessionStorage.setItem('session_expired_message', payload.message);
        }
      } catch (_) {}
      stopSessionWatcher();
      try { localStorage.removeItem('token'); } catch (_) {}
      try { localStorage.removeItem('user'); } catch (_) {}
      try { localStorage.removeItem('tenant_id'); } catch (_) {}
      try { localStorage.removeItem('login_at'); } catch (_) {}
      try { localStorage.removeItem('last_active_at'); } catch (_) {}
      try { window.location.href = '/'; } catch (_) { window.location.reload(); }
    }
    if (e.key === 'token') {
      const newToken = e.newValue;
      if (!newToken) {
        // Token was removed in another tab
        stopSessionWatcher();
        try { window.location.href = '/'; } catch (_) { window.location.reload(); }
      } else if (newToken !== e.oldValue) {
        // Token changed (login in another tab)
        try { window.location.reload(); } catch (_) {}
      }
    }
  } catch (err) {}
}

// User interaction handler
function handleUserInteraction(e) {
  // If mousemove, throttle and require real displacement
  if (e && (e.type === 'mousemove' || e.type === 'pointermove')) {
    const now = Date.now();
    if (now - lastMouseMoveCheck < 1000) return;
    lastMouseMoveCheck = now;
    const dx = Math.abs((e.clientX || 0) - lastMousePos.x);
    const dy = Math.abs((e.clientY || 0) - lastMousePos.y);
    if (dx < 3 && dy < 3) return;
    lastMousePos = { x: e.clientX || 0, y: e.clientY || 0 };
  }

  recordActivity('interaction');
}

export default function initSessionWatcher() {
  if (isInitialized) {
    startSessionWatcher();
    return () => stopSessionWatcher();
  }
  isInitialized = true;

  try {
    // If token exists, ensure last_active_at is initialized
    const token = localStorage.getItem('token');
    if (token && !localStorage.getItem('last_active_at')) {
      recordActivity('interaction');
    }

    // Storage events for cross-tab sync
    window.addEventListener('storage', onStorage);

    // Interaction events to detect active user
    const directEvents = ['click', 'mousedown', 'keydown', 'input', 'change', 'touchstart'];
    directEvents.forEach((ev) => {
      window.addEventListener(ev, handleUserInteraction, { passive: true });
    });

    // Capture scrolling on window and all scrollable subcontainers
    window.addEventListener('scroll', handleUserInteraction, { capture: true, passive: true });
    window.addEventListener('wheel', handleUserInteraction, { passive: true });

    // Pointer/mouse movements (throttled inside handler)
    window.addEventListener('mousemove', handleUserInteraction, { passive: true });
    window.addEventListener('pointermove', handleUserInteraction, { passive: true });

    // Text selection while reading
    document.addEventListener('selectionchange', handleUserInteraction, { passive: true });

    // Focus/visibility
    window.addEventListener('focus', () => {
      recordActivity('reading');
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        recordActivity('reading');
      }
    });

    startSessionWatcher();

    // Hook localStorage.setItem for token changes in this tab
    const origSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function (k, v) {
      origSetItem.apply(this, [k, v]);
      if (k === 'token') {
        if (v) {
          recordActivity('interaction');
          startSessionWatcher();
        } else {
          stopSessionWatcher();
        }
      }
    };
  } catch (e) {}

  return () => {
    try { window.removeEventListener('storage', onStorage); } catch (e) {}
    stopSessionWatcher();
  };
}
